import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/hooks/useStore';
import { useLocation, useParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { Label } from '@/components/common/Label';
import { isPathEk } from '@/utils/routes';
import ImageUploadShow from '@/components/general/ImageUploadShow';
import { ROLE_USER } from '@/types/enums';
import yup from '@/services/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { Checkbox } from '@/components/common/Checkbox';
import { CHECK_BIKE_BEFORE_RETURN_EK, CHECK_BIKE_BEFORE_RETURN_GAS } from '@/configs/constants';
import { Textarea } from '@/components/common/Textarea';
import { Logo } from '@/components/general/logo/Logo';
import SelectLanguageBtn from '@/components/general/select-language/SelectLanguageBtn';

export default observer(function ReservationReturnHistoryPage() {
    // hooks
    const { t } = useTranslation();
    const { id } = useParams();
    const { pathname } = useLocation();

    const isEk = isPathEk(pathname);
    const CHECK_BIKE_BEFORE_RETURN = isEk ? CHECK_BIKE_BEFORE_RETURN_EK : CHECK_BIKE_BEFORE_RETURN_GAS;

    // store
    const {
        reservationStore: {
            reservationsReturnHistory,
            getReservationsReturnHistory,
            setObservable
        },
        authStore: { profileGas, profileEk }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        returnOptions: yup.array().of(yup.number()).optional(),
        // parkedVehiclePosition: yup
        //     .number()
        //     .required('form_error_validate.required'),
        // bikeDamagePhotos: yup.array().of(yup.number()).optional(),
        note: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            returnOptions: [],
            // parkedVehiclePosition: undefined,
            // bikeDamagePhotos: [],
            note: ''
        }
    });

    // lifecycle
    useEffect(() => {
        onFetch(id);

        return () => {
            setObservable('reservationsReturnHistory', {});
        };
    }, [id]);

    // function
    const onFetch = async (id: string | undefined) => {
        if (id) {
            const res = await flowResult(getReservationsReturnHistory(Number(id)));
            if (res) {
                const { returnOptions, note } = res;
                form.reset({
                    returnOptions: returnOptions,
                    note: note || ''
                });
            }
        }
    };

    const isEnduser = useMemo(() => {
        if (
            (isEk && profileEk?.roleId === ROLE_USER.RENTAL_BIKE_OWNER_EK) ||
            (!isEk && profileGas?.roleId === ROLE_USER.RENTAL_BIKE_OWNER_RD)
        ) {
            return true;
        }
        return false;
    }, [profileGas, profileEk]);

    return (
        <div>
            <Form {...form}>
                <form>
                    <div className='layout-card'>
                        {isEnduser && (
                            <>
                                <div className='grid grid-cols-1 lg:grid-cols-3 gap-[min(24px,5vw)] lg:gap-3 justify-between items-center pb-[min(32px,6vw)]'>
                                    <div className='hidden lg:block'></div>
                                    <div className='flex justify-center'>
                                        <div className='logo w-[min(240px,55vw)]'>
                                            <Logo />
                                        </div>
                                    </div>
                                    <div className='flex justify-center lg:justify-end'>
                                        <SelectLanguageBtn />
                                    </div>
                                </div>
                                <div className='text-[min(24px,calc(100vw/17))] xsm:text-fs-mid24 leading-lh-mid24 font-semibold text-center whitespace-normal pb-[min(40px,6vw)]'>
                                    {t(`return_bike_form.title.${isEk ? 'title_ek' : 'title_gas'}`)}
                                </div>
                            </>
                        )}

                        <div className='text-left pb-[min(40px,6vw)]'>
                            {t('return_bike_form.description.des_1')}
                            <br />
                            {isEk ?
                                (
                                    <>
                                        {t('return_bike_form.description.des_2_ek')}
                                        <br className='block xsm:hidden' />
                                        {t('return_bike_form.description.des_3_ek')}
                                    </>
                                ) :
                                (
                                    <>
                                        {t('return_bike_form.description.des_2_gas')}
                                        <br className='block sm:hidden' />
                                        {t('return_bike_form.description.des_3_gas')}
                                    </>
                                )}
                        </div>

                        <div className='grid gap-[min(40px,6vw)]'>
                            <FormField
                                control={form.control}
                                name='returnOptions'
                                disabled
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('return_bike_form.check_box.title')}</FormLabel>
                                        <div className='grid gap-1.5 xsm:gap-2 ml-3'>
                                            {Object.values(CHECK_BIKE_BEFORE_RETURN).map(
                                                option => (
                                                    <div
                                                        key={option?.value}
                                                        className='flex items-center'
                                                    >
                                                        <FormControl>
                                                            <Checkbox
                                                                disabled
                                                                checked={
                                                                    field?.value?.includes(option.value) || false
                                                                }
                                                                onCheckedChange={(checked) => {
                                                                    if (checked) {
                                                                        field.onChange([
                                                                            ...(field?.value || []),
                                                                            option.value
                                                                        ]);
                                                                    } else {
                                                                        field.onChange(
                                                                            (field?.value || []).filter(
                                                                                item => item !== option.value
                                                                            )
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <Label className='ml-1.5 mb-0 cursor-pointer font-normal'>
                                                            {t(option.name)}
                                                        </Label>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <div>
                                <FormLabel required className='mb-0'>
                                    {t(`return_bike_form.field.${isEk ? 'bike_pictures_ek' : 'bike_pictures_gas'}`)}
                                </FormLabel>
                                <p className='mb-2'>
                                    駐車完了の状態で横向きの写真を撮影して下さい。
                                    <br />
                                    設置場所が分かりやすいよう、
                                    <br className='block sm:hidden' />
                                    可能な場合は5〜10mほど離れて撮影して下さい。
                                </p>
                                <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'>
                                    <ImageUploadShow
                                        urlImagePreview={
                                            reservationsReturnHistory.parkedVehiclePosition
                                                ?.filePath || ''
                                        }
                                    />
                                </div>
                            </div>

                            <div>
                                <FormLabel className='mb-0'>
                                    {t(`return_bike_form.field.${isEk ? 'image_scratches_falls_bike_ek' : 'image_scratches_falls_bike_gas'}`)}
                                </FormLabel>
                                <p className='mb-2'>
                                    {t(`return_bike_form.sentences.${isEk ? 'note_scratches_falls_bike_1_ek' : 'note_scratches_falls_bike_1_gas'}`)}
                                    <br className='block sm:hidden' />
                                    {t(`return_bike_form.sentences.${isEk ? 'note_scratches_falls_bike_2_ek' : 'note_scratches_falls_bike_2_gas'}`)}
                                    <br />
                                    <span className='text-[#737C7C]'>
                                        {t('return_bike_form.sentences.note_scratches_falls_bike_3')}
                                    </span>
                                </p>
                                <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'>
                                    {Array.from({ length: 3 }).map((_, index) => {
                                        const url =
                                            reservationsReturnHistory?.bikeDamagePhotos?.[index]
                                                ?.filePath || '';
                                        return (
                                            <ImageUploadShow key={index} urlImagePreview={url} />
                                        );
                                    })}
                                </div>
                            </div>

                            <FormField
                                control={form.control}
                                name='note'
                                disabled
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>
                                            {t('return_bike_form.field.comments')}
                                        </FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder={t('placeholder.enter')}
                                                className='min-h-32'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage className='mt-1' />
                                    </FormItem>
                                )}
                            />

                            <p className='text-center text-fs-mid13 xsm:text-fs-mid14'>
                                {isEk ? t('return_bike_form.sentences.note_ek') : t('return_bike_form.sentences.note_gas')}
                            </p>

                        </div>
                    </div>
                </form>
            </Form>
        </div>
    );
});
